import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Rule } from 'antd/lib/form';
import FormInput from 'components/Input/FormInput';

interface IProps {
	settingsId: string;
	onChange: (value: string) => void;
}

const MASTER_WALLET_FORMAT = /^[1-9]\d{1,8}$/;

const doubleLuhnDigit = (digit: number): number => (digit * 2 > 9 ? digit * 2 - 9 : digit * 2);

const hasValidLuhnCheckDigit = (digits: string): boolean => {
	const sum = digits
		.split('')
		.reverse()
		.reduce((total, char, positionFromRight) => {
			const digit = Number(char);
			const isDoubledPosition = positionFromRight % 2 === 1;
			return total + (isDoubledPosition ? doubleLuhnDigit(digit) : digit);
		}, 0);

	return sum % 10 === 0;
};

export const MasterWalletField: React.FC<IProps> = React.memo(({ onChange, settingsId }) => {
	const { t } = useTranslation('error');
	const validationRules = useMemo<Rule[]>(
		() => [
			{ required: false, pattern: MASTER_WALLET_FORMAT, message: t('masterWalletInvalid') },
			{
				validator: (_, value: Nullable<string>) =>
					!value || !MASTER_WALLET_FORMAT.test(value) || hasValidLuhnCheckDigit(value)
						? Promise.resolve()
						: Promise.reject(new Error(t('masterWalletInvalidCheckDigit'))),
			},
		],
		[t],
	);

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const trimmed = e.target.value.trim();
			onChange(trimmed);
			return trimmed;
		},
		[onChange],
	);

	return (
		<FormInput
			testId="MasterWalletField"
			name={`masterWallet-${settingsId}`}
			label="masterWalletLabel"
			getValueFromEvent={handleChange}
			maxLength={9}
			validationRules={validationRules}
		/>
	);
});
